import { Container, Graphics, GraphicsContext, Rectangle, Texture } from 'pixi.js';
import { GameMode } from '../../constants';
import { STAGES, StageCueKind } from '../../data/stages';
import {
  PERF_PHASE_FAMILY_SYNC,
  PERF_PHASE_GAME_FEEL,
  PERF_PHASE_LAYER_STACK,
  PERF_PHASE_PIXI_RENDER,
  PERF_RETIRE_BATCHING,
  PERF_RETIRE_IDLE,
  PERF_RETIRE_PENDING,
  PERF_RETIRE_UNLOADING,
  PERF_STAGE_IDLE,
  PERF_STAGE_PREPARING,
  PERF_STAGE_READY,
  type FramePhaseSampler,
} from '../../performance/FramePhaseSampler';
import type { GameWorld } from '../../world/GameWorld';
import {
  forgetLayerStack,
  loadLayerStack,
  loadR2Index,
  type LayerStackAsset,
  type R2Index,
} from '../layerstack/LayerStackLoader';
import type { RendererPort, RendererSettings } from '../port';
import {
  BOSS_SLUGS,
  ENEMY_SLUGS,
  GATE_SLUG,
  NODE_LENS_SLUGS,
  NODE_SLUG,
  PICKUP_SLUG,
  PLAYER_SLUG,
  PROJECTILE_SLUGS,
  entityStackSlugs,
  loadEntityStacks,
  rtPath,
} from './assets/registry';
import {
  loadMobileR2Base,
  loadMobileR2Stage,
  MOBILE_RETAINED_STAGE_SLUGS,
  MOBILE_SHARED_ENTITY_SLUGS,
  mobileStageSlugs,
  registerMobileR2Bundles,
  unloadMobileR2Stage,
} from './assets/AssetBundles';
import { StackRegistry } from './assets/StackRegistry';
import { MobilePoolObserver } from './MobilePoolObserver';
import { BossFamily } from './families/boss';
import { EnemiesFamily } from './families/enemies';
import { GateFamily } from './families/gate';
import { NodesFamily } from './families/nodes';
import { ParticlesFamily } from './families/particles';
import { PickupsFamily } from './families/pickups';
import { PlayerFamily } from './families/player';
import { ProjectilesFamily } from './families/projectiles';
import type { FrameContext } from './families/types';
import { FX_ONE_SHOT_SLUGS } from './fx/FxDirector';
import { GameFeelDirector } from './fx/GameFeelDirector';
import { MobileImpactPrimitives } from './fx/MobileImpactPrimitives';
import {
  createMobileMusicRailContexts,
  MusicVisualDirector,
  type R2MusicVisualClock,
} from './fx/MusicVisualDirector';
import { BloomStage } from './postfx/BloomStage';
import { R2Renderer, R2_HEIGHT, R2_WIDTH } from './R2Renderer';
import { WORLD_TO_R2 } from './scale';
import { stageBandSpecs } from './stage/bands';

/**
 * The R2 layer-stack renderer wired to the real GameWorld.
 *
 * This file is the composition root and nothing else: it owns the Pixi host,
 * the stack of Containers that fixes paint order, the per-frame Graphics and
 * the white flash. Every entity is drawn by a family module in families/, as
 * sprites parented either to that family's own layer Container or — for
 * segment projectiles, the node tether and boss telegraph lines, which have no
 * authored glyph to stretch — to the shared `beamLayer` Container every
 * family's init() is handed. Grade V's dedicated LinkLaserVisual is the one
 * exception: its lock reticle must remain legible over the tracked actor, so it
 * lives in the projectile family's own layer with additive blending. addChild
 * order fixes paint order once at scene-build time; there is no per-frame
 * draw-call order to preserve anymore. `overlay` is flash-only: it is cleared
 * and redrawn each frame by renderFlash().
 *
 * `layers` is exactly two children deep for one reason: bloom. Everything that
 * should glow lives in `glow`, which carries the AdvancedBloomFilter, and the
 * white flash sits outside it as a sibling. A full-frame white rect at alpha .7
 * is over any sane bloom threshold, so bloom on the whole stack would blow the
 * frame out on every big hit — the precise thing flashReduction exists to
 * prevent. Paint order inside `glow` is unchanged and still load-bearing.
 *
 * FX presentation objects are additive, entity sprites are not: pixel art must
 * stay opaque, or the ships turn into lanterns. The additive layers are also the
 * ones that clear the bloom threshold, which is what makes the glow read as
 * belonging to the effects.
 *
 * R2本編のカメラ位置は固定。GameFeelDirector は sim の旧 shake ticks を
 * 衝撃収束・リング・色収差へ変換し、このクラスは scene reset と visual scroll
 * だけを接続する。R2Renderer の trauma API は独立ハーネス互換のため残る。
 *
 * The ×2.5 simulation-to-display conversion lives in ./scale.
 */
const MAX_FRAME_MS = 100;
const FLASH_COLOR = 0xffffff;
const FLASH_REDUCED_CEILING = 0.16;
const FLASH_REDUCED_DIVISOR = 80;
const FLASH_CEILING = 0.7;
const FLASH_DIVISOR = 18;
/** BossFamily's longest renderer-local tail is 18 frames; leave margin before destroying its stack. */
const MOBILE_RETIRE_GRACE_FRAMES = 24;
const MOBILE_ALWAYS_WARM_SLUGS: readonly string[] = Object.freeze([
  PLAYER_SLUG,
  NODE_SLUG,
  PICKUP_SLUG,
  GATE_SLUG,
  ...PROJECTILE_SLUGS.filter((slug) => slug !== ''),
  ...NODE_LENS_SLUGS,
]);

export class R2GameRenderer implements RendererPort {
  rendererName = 'uninitialized';
  /**
   * Composed, not extended: R2Renderer.render() takes no world, and a subclass
   * cannot widen it into render(world). The band host stays exactly the object
   * the harness drives; this class only adds layers to it through addOverlay.
  */
  private readonly scene: R2Renderer;
  private readonly stacks: StackRegistry;
  private settings: RendererSettings = { shake: 1, flashReduction: false };
  private gameDisposed = false;
  private lastFrameMs = 0;
  private layers: Container | null = null;
  private beamLayer: Container | null = null;
  private overlay: Graphics | null = null;
  private readonly frameContext: FrameContext = Object.freeze({});
  /** startStage() rewinds simTick; a rewind means the scene, not the frame, changed. */
  private prevSimTick = 0;

  private readonly player = new PlayerFamily();
  private readonly gate = new GateFamily();
  private readonly nodes = new NodesFamily();
  private readonly enemies = new EnemiesFamily();
  private readonly projectiles = new ProjectilesFamily();
  private readonly pickups = new PickupsFamily();
  private readonly boss = new BossFamily();
  private readonly particles = new ParticlesFamily();
  /** Every raster loaded for this stage, collected for the init-time GPU warm-up. */
  private readonly loadedTextures: Texture[] = [];
  /** Backgrounds are visible immediately and always belong in mobile warm-up. */
  private readonly bandTextures: Texture[] = [];
  /** Entity textures indexed by authored slug for stage-scoped mobile warm-up. */
  private readonly texturesBySlug = new Map<string, readonly Texture[]>();
  private readonly warmedMobileStages = new Set<number>();
  private readonly preparedMobileStages = new Set<number>();
  private readonly preparingMobileStages = new Map<number, Promise<void>>();
  private readonly warmingMobileStages = new Map<number, Promise<void>>();
  private readonly retiredMobileStages = new Set<number>();
  private r2Index: R2Index | null = null;
  private mobileStage = 0;
  private pendingRetireStage = -1;
  private pendingRetainStage = -1;
  private stageResultFrames = 0;
  private retiringStage = -1;
  private retirementUnloading = false;
  private retirementCompletion: Promise<void> | null = null;
  private resolveRetirementCompletion: (() => void) | null = null;
  private readonly retireSlugs: string[] = [];
  private retireSlugCursor = 0;
  private readonly retireSources: Texture['source'][] = [];
  private retireSourceCursor = 0;
  private readonly retiredTextures = new Set<Texture>();
  private readonly retiredSources = new Set<Texture['source']>();
  private readonly gameFeel: GameFeelDirector;
  private readonly musicVisual: MusicVisualDirector;
  private readonly bloom = new BloomStage();
  private readonly mobilePerformanceMode: boolean;
  private readonly mobilePools: MobilePoolObserver | null;
  private mobileImpact: MobileImpactPrimitives | null = null;
  private readonly mobileOwnedContexts: GraphicsContext[] = [];

  constructor(musicClock: R2MusicVisualClock) {
    this.scene = new R2Renderer();
    this.mobilePerformanceMode = this.scene.mobilePerformanceMode;
    this.mobilePools = this.mobilePerformanceMode ? new MobilePoolObserver() : null;
    this.stacks = new StackRegistry(this.mobilePerformanceMode);
    this.gameFeel = new GameFeelDirector(this.mobilePerformanceMode);
    this.musicVisual = new MusicVisualDirector(
      musicClock,
      this.scene,
      this.mobilePerformanceMode || musicClock.usesHTMLMediaFallback === true,
    );
  }

  async init(canvas: HTMLCanvasElement): Promise<void> {
    await this.scene.init(canvas);
    if (this.gameDisposed) return;
    const index = await loadR2Index();
    if (this.gameDisposed) return;
    this.r2Index = index;
    if (this.mobilePerformanceMode) {
      registerMobileR2Bundles(index);
      await loadMobileR2Base();
      if (this.gameDisposed) return;
    }
    await this.addBands(index);
    if (this.gameDisposed) return;
    await this.registerEntityStacks(index);
    if (this.gameDisposed) return;
    // The impact sprites are one-shot pools, not slot-bound stacks, so they load
    // through the same loader but stay out of StackRegistry's per-slot bookkeeping.
    const fxAssets = await loadEntityStacks(index, FX_ONE_SHOT_SLUGS);
    if (this.gameDisposed) return;
    const fxTextures: Texture[] = [];
    for (const asset of fxAssets) {
      for (const texture of asset.textures.values()) {
        this.loadedTextures.push(texture);
        fxTextures.push(texture);
      }
    }
    this.buildScene(fxAssets);
    if (this.mobilePerformanceMode) {
      // Keep the native 960×540 image and every authored layer. The mobile
      // saving is residency timing: upload only always-visible/shared art and
      // stage 1 now; later stages are uploaded behind their result screen.
      const initialTextures = new Set<Texture>(this.bandTextures);
      for (const texture of fxTextures) initialTextures.add(texture);
      for (const slug of MOBILE_ALWAYS_WARM_SLUGS) this.addSlugTextures(initialTextures, slug);
      this.addStageTextures(initialTextures, 0);
      this.preparedMobileStages.add(0);
      await this.scene.warmupTextures([...initialTextures]);
      if (this.gameDisposed) return;
      this.warmedMobileStages.add(0);
      this.warmupMobilePrimitives();
    } else {
      // Desktop keeps the exact established eager warm-up path.
      await this.scene.warmupTextures(this.loadedTextures);
    }
    if (this.gameDisposed) return;
    this.rendererName = `WebGL2 / PixiJS ${R2_WIDTH}×${R2_HEIGHT} (R2 layer stacks, ${WORLD_TO_R2}× of 384×216)`;
  }

  /**
   * May land before init() has built the scene, so both sinks are written
   * unconditionally and each ignores the call while its own stage is absent;
   * buildScene() re-reads `this.settings` so a pre-init call is never lost.
   */
  setSettings(settings: RendererSettings): void {
    // Mobile always uses reduced impact geometry. Desktop receives the exact
    // user settings object and remains visually unchanged.
    this.settings = this.mobilePerformanceMode
      ? { shake: settings.shake, flashReduction: true }
      : settings;
    this.bloom.setReduced(this.settings.flashReduction);
    this.scene.setPostReduction(this.settings.flashReduction);
  }

  render(world: GameWorld, performanceSampler: FramePhaseSampler | null = null): void {
    const overlay = this.overlay;
    if (!this.layers || !overlay) return;
    if (this.mobilePerformanceMode) this.syncMobileStageAssets(world);
    const now = performance.now();
    const deltaMs = this.lastFrameMs === 0 ? 0 : Math.min(MAX_FRAME_MS, now - this.lastFrameMs);
    this.lastFrameMs = now;

    // A stage start rewinds the sim clock and resets every pool. Renderer-local
    // tails (the boss death pose) belong to the scene that just ended, not the
    // one being drawn, so they are dropped rather than carried across.
    if (world.simTick < this.prevSimTick) {
      this.boss.onSceneReset();
      this.player.onSceneReset();
      this.gate.onSceneReset();
      this.gameFeel.onSceneReset();
      this.musicVisual.onSceneReset();
      this.mobilePools?.onSceneReset();
    }
    this.prevSimTick = world.simTick;
    if (!this.mobilePerformanceMode) overlay.clear();

    // Each family repositions/toggles its own pooled sprites (beamLayer or its
    // own layer); overlay only gets the flash. Do not reorder — see the class
    // doc comment.
    const familyStart = performanceSampler ? performance.now() : 0;
    this.mobilePools?.sync(world);
    const frame = this.frameContext;
    this.gate.sync(world, frame);
    this.player.sync(world, frame);
    this.nodes.sync(world, frame);
    this.enemies.sync(world, frame);
    this.projectiles.sync(world, frame);
    this.pickups.sync(world, frame);
    this.boss.sync(world, frame);
    this.nodes.syncTether(world, frame);
    this.particles.sync(world, frame);
    if (performanceSampler) {
      performanceSampler.recordRenderPhase(
        PERF_PHASE_FAMILY_SYNC,
        performance.now() - familyStart,
      );
    }
    if (this.mobilePerformanceMode) this.retirePendingMobileStage(world.mode);

    // After the families and before scene.update: sync() is what advances the
    // combo boost, so reading visualScrollX any earlier would lay the parallax
    // out with last frame's boost and blunt every tier crossing. Both directors
    // keep the main-game root at scale 1; impact is light geometry, never camera motion.
    const feelStart = performanceSampler ? performance.now() : 0;
    this.gameFeel.sync(world, deltaMs, this.settings);
    this.musicVisual.sync(world, deltaMs, this.settings);
    this.renderFlash(world, overlay);
    this.scene.scrollX = this.gameFeel.visualScrollX(world) * WORLD_TO_R2;
    if (performanceSampler) {
      performanceSampler.recordRenderPhase(
        PERF_PHASE_GAME_FEEL,
        performance.now() - feelStart,
      );
    }
    const stackStart = performanceSampler ? performance.now() : 0;
    this.scene.update(deltaMs);
    this.stacks.advanceVisible(deltaMs);
    if (performanceSampler) {
      performanceSampler.recordRenderPhase(
        PERF_PHASE_LAYER_STACK,
        performance.now() - stackStart,
      );
    }
    const pixiStart = performanceSampler ? performance.now() : 0;
    this.scene.render();
    if (performanceSampler) {
      performanceSampler.recordRenderPhase(
        PERF_PHASE_PIXI_RENDER,
        performance.now() - pixiStart,
      );
      const pools = this.mobilePools;
      performanceSampler.recordRenderCounts(
        pools?.projectiles.activeCount ?? world.projectiles.indices.count,
        pools?.particles.activeCount ?? world.particles.indices.count,
        pools?.enemies.activeCount ?? world.enemies.indices.count,
        pools?.pickups.activeCount ?? world.pickups.indices.count,
        this.gameFeel.activeOneShotCount,
      );
      const nextStage = world.currentStage + 1;
      const preparing =
        this.preparingMobileStages.has(nextStage)
        || this.warmingMobileStages.has(nextStage);
      const prepareState = preparing
        ? PERF_STAGE_PREPARING
        : this.warmedMobileStages.has(nextStage)
          ? PERF_STAGE_READY
          : PERF_STAGE_IDLE;
      performanceSampler.recordStageState(
        prepareState,
        this.retirementUnloading
          ? PERF_RETIRE_UNLOADING
          : this.retiringStage >= 0
            ? PERF_RETIRE_BATCHING
            : this.pendingRetireStage >= 0
              ? PERF_RETIRE_PENDING
              : PERF_RETIRE_IDLE,
      );
    }
  }

  dispose(): void {
    if (this.gameDisposed) return;
    this.gameDisposed = true;
    this.resolveRetirementCompletion?.();
    this.resolveRetirementCompletion = null;
    this.retirementCompletion = null;
    const layers = this.layers;
    const mobileImpact = this.mobileImpact;
    this.gate.dispose();
    this.player.dispose();
    this.nodes.dispose();
    this.enemies.dispose();
    this.projectiles.dispose();
    this.pickups.dispose();
    this.boss.dispose();
    this.particles.dispose();
    this.gameFeel.dispose();
    this.musicVisual.dispose();
    // Before layers.destroy(): the filter is not a child, so the cascade below
    // would leave it holding its render targets.
    this.bloom.dispose();
    this.stacks.clear();
    this.layers = null;
    this.beamLayer = null;
    this.overlay = null;
    this.loadedTextures.length = 0;
    this.bandTextures.length = 0;
    this.texturesBySlug.clear();
    this.warmedMobileStages.clear();
    this.preparedMobileStages.clear();
    this.preparingMobileStages.clear();
    this.warmingMobileStages.clear();
    this.retiredMobileStages.clear();
    this.pendingRetireStage = -1;
    this.pendingRetainStage = -1;
    this.retiringStage = -1;
    this.retirementUnloading = false;
    this.retireSlugs.length = 0;
    this.retireSources.length = 0;
    this.retiredTextures.clear();
    this.retiredSources.clear();
    this.r2Index = null;
    this.mobileImpact = null;
    if (layers) layers.destroy({ children: true });
    mobileImpact?.disposeContexts();
    for (let i = 0; i < this.mobileOwnedContexts.length; i += 1) {
      this.mobileOwnedContexts[i]!.destroy();
    }
    this.mobileOwnedContexts.length = 0;
    this.scene.dispose();
  }

  private async addBands(index: R2Index): Promise<void> {
    // Sequential: addBand appends to the band layer, so call order is z order.
    for (const spec of stageBandSpecs(index)) {
      await this.scene.addBand(spec);
      if (this.gameDisposed) return;
      // loadLayerStack caches by rung path, so this is the same asset addBand
      // just loaded above, not a second fetch — just a way to reach its
      // texture map for the warm-up list.
      const asset = await loadLayerStack(spec.rungPath);
      if (this.gameDisposed) return;
      for (const texture of asset.textures.values()) {
        this.loadedTextures.push(texture);
        this.bandTextures.push(texture);
      }
    }
  }

  private async registerEntityStacks(index: R2Index): Promise<void> {
    const slugs = this.mobilePerformanceMode
      ? [...new Set([...MOBILE_SHARED_ENTITY_SLUGS, ...mobileStageSlugs(0)])]
      : entityStackSlugs();
    const assets = await loadEntityStacks(index, slugs);
    if (this.gameDisposed) return;
    this.stacks.register(slugs, assets);
    for (let i = 0; i < assets.length; i += 1) {
      const textures = [...assets[i]!.textures.values()];
      this.texturesBySlug.set(slugs[i]!, textures);
      this.loadedTextures.push(...textures);
    }
  }

  private addSlugTextures(target: Set<Texture>, slug: string): void {
    const textures = this.texturesBySlug.get(slug);
    if (!textures) return;
    for (const texture of textures) target.add(texture);
  }

  private addStageTextures(target: Set<Texture>, stageId: number): void {
    const stage = STAGES[stageId];
    if (!stage) return;
    for (const cue of stage.cues) {
      if (cue.kind === StageCueKind.SpawnWave) {
        const slug = ENEMY_SLUGS[cue.a];
        if (slug) this.addSlugTextures(target, slug);
      } else if (cue.kind === StageCueKind.MidBoss || cue.kind === StageCueKind.FinalBoss) {
        const slug = BOSS_SLUGS[cue.a];
        if (slug) this.addSlugTextures(target, slug);
      }
    }
  }

  private warmMobileStage(stageId: number): Promise<void> {
    if (this.warmedMobileStages.has(stageId) || !STAGES[stageId]) return Promise.resolve();
    const existing = this.warmingMobileStages.get(stageId);
    if (existing) return existing;
    const pending = this.prepareMobileStage(stageId)
      .then(async () => {
        if (this.gameDisposed) return;
        const textures = new Set<Texture>();
        this.addStageTextures(textures, stageId);
        await this.scene.warmupTextures([...textures]);
      })
      .then(() => {
        if (!this.gameDisposed) this.warmedMobileStages.add(stageId);
      })
      .finally(() => {
        this.warmingMobileStages.delete(stageId);
      });
    this.warmingMobileStages.set(stageId, pending);
    return pending;
  }

  private syncMobileStageAssets(world: GameWorld): void {
    const stageId = world.currentStage;
    if (stageId !== this.mobileStage) {
      const previous = this.mobileStage;
      if (
        !this.retiredMobileStages.has(previous)
        && previous !== this.retiringStage
        && previous !== this.pendingRetireStage
        && this.pendingRetireStage < 0
      ) {
        this.pendingRetireStage = previous;
        this.pendingRetainStage = stageId;
      }
      this.mobileStage = stageId;
      if (!this.warmedMobileStages.has(stageId)) {
        void this.warmMobileStage(stageId).catch(() => undefined);
      }
    }
    const stage = STAGES[stageId];
    if (!stage) return;
    const nextStage = stageId + 1;
    const resultWindow = world.mode === GameMode.StageResult;
    const carriedResultWindow =
      this.stageResultFrames > 0
      && (
        world.mode === GameMode.Ending
        || world.mode === GameMode.Title
        || world.mode === GameMode.GameOver
      );
    if (resultWindow || carriedResultWindow) {
      this.stageResultFrames += 1;
      if (
        resultWindow
        && STAGES[nextStage]
        && !this.warmedMobileStages.has(nextStage)
        && !this.warmingMobileStages.has(nextStage)
      ) {
        // Decode and GPU upload live behind the result presentation instead of
        // causing a surprise main-thread decode during the preceding combat.
        void this.warmMobileStage(nextStage).catch(() => undefined);
      }
      const nextReady = !STAGES[nextStage] || this.warmedMobileStages.has(nextStage);
      if (
        this.stageResultFrames >= MOBILE_RETIRE_GRACE_FRAMES
        && nextReady
        && !this.retiredMobileStages.has(stageId)
        && this.retiringStage < 0
        && (this.pendingRetireStage < 0 || this.pendingRetireStage === stageId)
      ) {
        this.pendingRetireStage = stageId;
        this.pendingRetainStage = STAGES[nextStage] ? nextStage : -1;
      }
    } else {
      this.stageResultFrames = 0;
    }
  }

  async prepareForStageAdvance(): Promise<void> {
    if (!this.mobilePerformanceMode) return;
    const nextStage = this.mobileStage + 1;
    if (!STAGES[nextStage]) return;
    await this.warmMobileStage(nextStage);
  }

  async prepareForRunStart(): Promise<void> {
    if (!this.mobilePerformanceMode) return;
    const drain = new Uint8Array(STAGES.length);
    for (let stageId = 1; stageId < STAGES.length; stageId += 1) {
      if (
        stageId === this.mobileStage
        || stageId === this.pendingRetireStage
        || stageId === this.retiringStage
        || this.preparedMobileStages.has(stageId)
        || this.warmedMobileStages.has(stageId)
        || this.preparingMobileStages.has(stageId)
        || this.warmingMobileStages.has(stageId)
      ) {
        drain[stageId] = 1;
      }
    }

    // A StageResult → Title action can race the ahead-of-current warm (notably
    // stage 3's ~30 MiB Mnemosyne multipack). Let that work settle behind Title
    // first, then retire every touched nonzero stage serially.
    for (let stageId = 1; stageId < STAGES.length; stageId += 1) {
      if (drain[stageId] === 0) continue;
      const warming = this.warmingMobileStages.get(stageId);
      if (warming) await warming.catch(() => undefined);
      const preparing = this.preparingMobileStages.get(stageId);
      if (preparing) await preparing.catch(() => undefined);
    }
    for (let stageId = 1; stageId < STAGES.length; stageId += 1) {
      if (drain[stageId] !== 0) await this.retireMobileStageBeforeRun(stageId);
    }
  }

  private async retireMobileStageBeforeRun(stageId: number): Promise<void> {
    // Title keeps rendering while this waits, so the retirement state machine
    // can finish slug/source micro-batches and the final async bundle unload.
    while (!this.gameDisposed && !this.retiredMobileStages.has(stageId)) {
      if (
        this.pendingRetireStage < 0
        && this.retiringStage < 0
        && !this.retirementUnloading
      ) {
        this.pendingRetireStage = stageId;
        this.pendingRetainStage = 0;
      }
      await this.waitForRetirementCompletion();
    }
  }

  private prepareMobileStage(stageId: number): Promise<void> {
    if (this.preparedMobileStages.has(stageId) || !STAGES[stageId]) return Promise.resolve();
    const existing = this.preparingMobileStages.get(stageId);
    if (existing) return existing;
    const index = this.r2Index;
    if (!index) return Promise.resolve();
    // A reload attempt owns the stage bundle even if decode later rejects.
    // Clear the retired marker now so Title drain can clean partial residency.
    this.retiredMobileStages.delete(stageId);
    const pending = (async () => {
      await loadMobileR2Stage(stageId);
      if (this.gameDisposed) return;
      const slugs = mobileStageSlugs(stageId);
      const assets = await loadEntityStacks(index, slugs);
      if (this.gameDisposed) return;
      this.stacks.register(slugs, assets);
      for (let i = 0; i < slugs.length; i += 1) {
        const textures = [...assets[i]!.textures.values()];
        this.texturesBySlug.set(slugs[i]!, textures);
      }
      this.preparedMobileStages.add(stageId);
    })().finally(() => {
      this.preparingMobileStages.delete(stageId);
    });
    this.preparingMobileStages.set(stageId, pending);
    return pending;
  }

  private retirePendingMobileStage(mode: GameMode): void {
    if (
      mode !== GameMode.StageResult
      && mode !== GameMode.GameOver
      && mode !== GameMode.Ending
      && mode !== GameMode.Title
    ) {
      return;
    }
    if (this.retirementUnloading) return;
    if (this.retiringStage < 0) {
      const pending = this.pendingRetireStage;
      if (pending < 0) return;
      this.beginMobileStageRetirement(pending, this.pendingRetainStage);
      this.pendingRetireStage = -1;
      this.pendingRetainStage = -1;
    }
    const index = this.r2Index;
    if (!index || this.retiringStage < 0) return;

    // One slug per non-playing frame. Families synchronized immediately before
    // this call, so every retired sprite is already detached and free.
    if (this.retireSlugCursor < this.retireSlugs.length) {
      const slug = this.retireSlugs[this.retireSlugCursor]!;
      if (!this.stacks.unregister(slug)) return;
      const textures = this.texturesBySlug.get(slug);
      if (textures) {
        for (const texture of textures) {
          this.retiredTextures.add(texture);
          const source = texture.source;
          if (!this.retiredSources.has(source)) {
            this.retiredSources.add(source);
            this.retireSources.push(source);
          }
        }
      }
      this.retireSlugCursor += 1;
      this.texturesBySlug.delete(slug);
      forgetLayerStack(rtPath(index, slug));
      return;
    }

    // Explicitly release one deduplicated GPU source per result frame. Linked
    // multipack pages are included because the sources came from every texture,
    // not only the root atlas descriptor.
    if (this.retireSourceCursor < this.retireSources.length) {
      this.retireSources[this.retireSourceCursor]!.unload();
      this.retireSourceCursor += 1;
      return;
    }

    for (let i = this.loadedTextures.length - 1; i >= 0; i -= 1) {
      if (!this.retiredTextures.has(this.loadedTextures[i]!)) continue;
      const last = this.loadedTextures.pop();
      if (i < this.loadedTextures.length && last) this.loadedTextures[i] = last;
    }
    const retiredStage = this.retiringStage;
    this.preparedMobileStages.delete(retiredStage);
    this.warmedMobileStages.delete(retiredStage);
    this.warmingMobileStages.delete(retiredStage);
    this.retirementUnloading = true;
    void unloadMobileR2Stage(retiredStage)
      .catch(() => undefined)
      .finally(() => {
        this.retirementUnloading = false;
        this.retiredMobileStages.add(retiredStage);
        this.finishMobileStageRetirement();
      });
  }

  private beginMobileStageRetirement(stageId: number, retainStageId: number): void {
    const index = this.r2Index;
    if (!index) return;
    const retained = new Set([
      ...MOBILE_SHARED_ENTITY_SLUGS,
      ...MOBILE_RETAINED_STAGE_SLUGS,
      // A run may return to Title and restart without recreating the renderer.
      // Pin stage 0 so that path never points at an unloaded first-stage pool.
      ...mobileStageSlugs(0),
      ...mobileStageSlugs(retainStageId),
    ]);
    for (const slug of mobileStageSlugs(stageId)) {
      if (!retained.has(slug)) this.retireSlugs.push(slug);
    }
    if (this.retireSlugs.length === 0) {
      this.retiredMobileStages.add(stageId);
      this.pendingRetireStage = -1;
      this.pendingRetainStage = -1;
      this.finishMobileStageRetirement();
      return;
    }
    this.retiringStage = stageId;
  }

  private finishMobileStageRetirement(): void {
    this.retiringStage = -1;
    this.retireSlugs.length = 0;
    this.retireSlugCursor = 0;
    this.retireSources.length = 0;
    this.retireSourceCursor = 0;
    this.retiredTextures.clear();
    this.retiredSources.clear();
    this.resolveRetirementCompletion?.();
    this.resolveRetirementCompletion = null;
    this.retirementCompletion = null;
  }

  private waitForRetirementCompletion(): Promise<void> {
    if (!this.retirementCompletion) {
      this.retirementCompletion = new Promise<void>((resolve) => {
        this.resolveRetirementCompletion = resolve;
      });
    }
    return this.retirementCompletion;
  }

  /**
   * Paint order, far to near. A LayerStackSprite is a container, so depth is
   * child order and each family gets its own layer: the ship must not end up
   * behind an enemy just because its sprite was built first. `beamLayer` sits
   * at the very bottom — where the old retained `beams` Graphics sat — and is
   * shared by projectiles/boss/nodes; the order those three init() calls run
   * in below fixes the relative depth of fallback shots, telegraph lines and
   * the tether inside it (fallback shots lowest, telegraph above them, tether
   * on top), matching the old per-frame draw-call order into that Graphics.
   */
  private buildScene(fxAssets: readonly LayerStackAsset[]): void {
    const layers = new Container();
    // Everything bloom is allowed to see. The flash overlay is deliberately not
    // in here — see the class comment.
    const glow = new Container();
    // The calibration-gate structure: world scenery behind every entity and
    // every beam, inside `glow` so its amber emissives bloom with the rest.
    const gate = new Container();
    const beamLayer = new Container();
    // Behind every entity and in front of the background bands: streaks are
    // depth cues, so anything the player reads must occlude them.
    const speedLines = new Graphics();
    const pickups = new Container();
    const bosses = new Container();
    const enemies = new Container();
    const nodes = new Container();
    // The engine trail belongs behind the ship that emits it, but in front of
    // the background: it is a cue about the player, not about the stage.
    const exhaust = new Graphics();
    const player = new Container();
    const projectiles = new Container();
    const particles = new Container();
    // Impact FX above the entities they belong to, still below `overlay` —
    // the flash stays the topmost thing and stays the only thing in overlay.
    const bursts = new Container();
    const sparks = new Container();
    const muzzle = new Graphics();
    // Shock rings above the debris that shares their moment, below the muzzle
    // flash: a ring is an aftermath, the barrel spark is a present-tense cue.
    const mobileImpact = this.mobilePerformanceMode ? new MobileImpactPrimitives() : null;
    const ringsGraphics = this.mobilePerformanceMode ? null : new Graphics();
    const ringsLayer = mobileImpact?.container ?? ringsGraphics!;
    // 音楽同期演出は一枚のリング語彙だけを使い、既存衝撃リングより上・銃口より下。
    const musicRailContexts = this.mobilePerformanceMode
      ? createMobileMusicRailContexts()
      : null;
    if (musicRailContexts) this.mobileOwnedContexts.push(...musicRailContexts);
    const musicField = musicRailContexts
      ? new Graphics({
          context: musicRailContexts[0],
          roundPixels: true,
          autoGarbageCollect: false,
        })
      : new Graphics();
    // comboの溜めだけを担う非加算レイヤー。overlayの白フラッシュとは混ぜない。
    const musicShade = new Graphics();
    let overlay: Graphics;
    if (this.mobilePerformanceMode) {
      const flashContext = new GraphicsContext()
        .rect(0, 0, R2_WIDTH, R2_HEIGHT)
        .fill(FLASH_COLOR);
      flashContext.autoGarbageCollect = false;
      this.mobileOwnedContexts.push(flashContext);
      overlay = new Graphics({
        context: flashContext,
        roundPixels: true,
        autoGarbageCollect: false,
      });
      overlay.visible = false;
    } else {
      overlay = new Graphics();
    }
    glow.addChild(
      gate,
      beamLayer,
      speedLines,
      pickups,
      bosses,
      enemies,
      nodes,
      exhaust,
      player,
      projectiles,
      particles,
      bursts,
      sparks,
      ringsLayer,
      musicField,
      muzzle,
    );
    layers.addChild(glow, musicShade, overlay);
    // Additive on the light, never on the art. `particles`, `bursts`, `sparks`
    // and `beamLayer` are Containers, so the mode propagates to every child
    // sprite they pool without any family knowing about it.
    for (const additive of [beamLayer, speedLines, exhaust, particles, bursts, sparks, ringsLayer, musicField, muzzle]) {
      additive.blendMode = 'add';
    }
    // Pinned so bloom measures a fixed region instead of re-deriving scene
    // bounds every frame — entities routinely sit outside the frame, and a
    // region that breathes means a render target that is reallocated.
    glow.filterArea = new Rectangle(0, 0, R2_WIDTH, R2_HEIGHT);
    if (!this.mobilePerformanceMode) this.bloom.attach(glow, this.settings.flashReduction);
    this.scene.setPostReduction(this.settings.flashReduction);
    this.scene.addOverlay(layers);
    this.layers = layers;
    this.beamLayer = beamLayer;
    this.overlay = overlay;
    this.mobileImpact = mobileImpact;

    const stacks = this.stacks;
    const mobilePerformanceMode = this.mobilePerformanceMode;
    const mobilePools = this.mobilePools;
    this.gate.init({ stacks, layer: gate, beamLayer, mobilePerformanceMode, mobilePools });
    this.player.init({ stacks, layer: player, beamLayer, mobilePerformanceMode, mobilePools });
    this.pickups.init({ stacks, layer: pickups, beamLayer, mobilePerformanceMode, mobilePools });
    this.enemies.init({ stacks, layer: enemies, beamLayer, mobilePerformanceMode, mobilePools });
    this.projectiles.init({ stacks, layer: projectiles, beamLayer, mobilePerformanceMode, mobilePools });
    this.boss.init({ stacks, layer: bosses, beamLayer, mobilePerformanceMode, mobilePools });
    this.nodes.init({ stacks, layer: nodes, beamLayer, mobilePerformanceMode, mobilePools });
    // Particles above projectiles, below the overlay flash — the same slot
    // the old overlay.rect() drawing painted into.
    this.particles.init({ stacks, layer: particles, beamLayer, mobilePerformanceMode, mobilePools });
    this.gameFeel.init({
      host: this.scene,
      mobilePools,
      sparkLayer: sparks,
      burstLayer: bursts,
      speedLines,
      exhaust,
      muzzle,
      rings: ringsGraphics,
      mobileImpact,
      fxSlugs: FX_ONE_SHOT_SLUGS,
      fxAssets,
    });
    this.musicVisual.init(musicField, musicShade, musicRailContexts);
  }

  private warmupMobilePrimitives(): void {
    const impact = this.mobileImpact;
    if (!impact) return;
    const contexts: GraphicsContext[] = [];
    impact.collectWarmupContexts(contexts);
    for (let i = 0; i < this.mobileOwnedContexts.length; i += 1) {
      contexts.push(this.mobileOwnedContexts[i]!);
    }
    try {
      this.scene.warmupGraphicsContexts(contexts);
    } catch {
      // The public offscreen render is an optimization only. If a browser
      // rejects its scratch target, retained contexts still render normally.
    }
  }

  private renderFlash(world: GameWorld, overlay: Graphics): void {
    const flash = this.settings.flashReduction
      ? Math.min(FLASH_REDUCED_CEILING, world.globalFlashTicks / FLASH_REDUCED_DIVISOR)
      : Math.min(FLASH_CEILING, world.globalFlashTicks / FLASH_DIVISOR);
    if (this.mobilePerformanceMode) {
      overlay.alpha = flash;
      overlay.visible = flash > 0;
      return;
    }
    if (flash <= 0) return;
    overlay.rect(0, 0, R2_WIDTH, R2_HEIGHT);
    overlay.fill({ color: FLASH_COLOR, alpha: flash });
  }
}
